import { Component, inject } from '@angular/core';
import { IconComponent } from '@kin/spark-ui';
import { SignupCardComponent } from '../ui/signup-card.component';
import { ContactFieldsComponent } from '../contact/contact-fields.component';
import { ContactConsentsComponent } from '../contact/contact-consents.component';
import { FormCtaComponent } from '../ui/form-cta.component';
import { ContactFormBase } from '../contact/contact-form-base';
import { ContactSubmission, SignupSubmitService } from '../intake/signup-submit.service';
import { SignupResetService } from '../signup-reset.service';

/** Shown when the address is missing or outside the service area; collects contact info for launch alerts. */
@Component({
  selector: 'app-waitlist-form',
  standalone: true,
  imports: [IconComponent, SignupCardComponent, ContactFieldsComponent, ContactConsentsComponent, FormCtaComponent],
  template: `
    <app-signup-card [decorated]="true">
      <!-- Approved pairing: Yellow-600 tile with Neutral-1000 icon -->
      <div class="icon-tile"><ngk-icon icon="mail-check" size="28px" /></div>

      <h1>We're not servicing your address yet.</h1>
      <p class="body">Right now Kin's property tax protest service is available for a limited set of locations, and we can't file in yours just yet.</p>
      <p class="body last">We're expanding coverage before next tax season. Leave your info and we'll email you the moment your location is eligible, so you're first in line.</p>

      @if (addressStore.hasAddress()) {
        <div class="address-on-file">
          <ngk-icon icon="house" size="16px" />
          <div>
            <div class="address-label">Address on file</div>
            <div class="address-value">
              {{ addressStore.singleLineAddress() }} · <a [href]="reset.landingUrl" (click)="reset.onLandingLinkClick($event)">change</a>
            </div>
          </div>
        </div>
      }

      <form (submit)="onSubmit($event)" novalidate>
        <app-contact-fields [(value)]="contact" [(errors)]="errors" [phoneRequired]="phoneRequired" />
        <app-contact-consents [(value)]="contact" [(errors)]="errors" />
        <app-form-cta [label]="ctaLabel" [submittingLabel]="ctaSubmittingLabel" icon="bell"
          [reassurance]="reassurance" [submitting]="submitting()" [error]="submitError()" />
      </form>
    </app-signup-card>
  `,
  styles: `
    .icon-tile {
      width: 56px; height: 56px; border-radius: 16px; margin-bottom: 20px;
      display: flex; align-items: center; justify-content: center;
      background: var(--kin-yellow-600); color: var(--kin-neutral-1000);
    }
    /* Heading 1: 40px / 700 / -1.5px */
    h1 { font-size: 40px; font-weight: 700; line-height: 1.1; letter-spacing: -1.5px; margin: 0 0 16px; }
    /* Body Large: 18px / 1.7 */
    .body { font-size: 18px; line-height: 1.7; color: var(--kin-text-secondary); margin: 0 0 12px; }
    .body.last { margin-bottom: 24px; }
    /* Yellow-100 callout with Yellow-600 rule; text stays Neutral (AA on Yellow-100) */
    .address-on-file {
      display: flex; gap: 12px; align-items: flex-start; padding: 14px 18px; margin-bottom: 28px;
      background: var(--kin-yellow-100); border-left: 3px solid var(--kin-yellow-600); border-radius: var(--kin-radius-md);
      font-size: 14px; line-height: 1.5;
    }
    .address-on-file ngk-icon { flex-shrink: 0; margin-top: 2px; }
    .address-label { font-weight: 700; margin-bottom: 2px; }
    .address-value { color: var(--kin-text-secondary); }
    .address-value a { font-weight: 600; }
    @media (max-width: 600px) {
      h1 { font-size: 28px; letter-spacing: -0.5px; }
      .body { font-size: 16px; line-height: 1.6; }
    }
  `,
})
export class WaitlistFormComponent extends ContactFormBase {
  private submitService = inject(SignupSubmitService);

  // "change" re-enters the address on the landing page, same as Back (the address comes from the querystring)
  protected readonly reset = inject(SignupResetService);

  // `POST /customer-intake` requires a phone number
  protected readonly phoneRequired = true;

  ctaLabel = "Notify me when it's live";
  ctaSubmittingLabel = 'Adding…';
  reassurance = 'Secure · Unsubscribe any time';

  protected send(submission: ContactSubmission) {
    return this.submitService.joinWaitlist(submission);
  }
}
